import { log } from '@/log'
import { IncorrectAuthenticationDataError, TooManyRequestedError } from '@spa/components/pages/auth/login/model/error'
import {
  AuthenticationData,
  InitializeFailure,
  LoginFailure,
  State,
} from '@spa/components/pages/auth/login/model/state'
import { Either, left, right } from 'fp-ts/lib/Either'
import { LoginStateStore } from './interaction/initialize'
import { LoginGateway } from './interaction/login'

type LoginResult = Either<Interaction<LoginFailure>, Interaction<'login-succeed'>>

export class Interaction<T extends State = State> {
  static create(): Interaction<'not-initialized'> {
    return new Interaction('not-initialized')
  }

  private constructor(readonly state: T) {}

  /**
   * この箇所ですでにストアに本会員ユーザ情報が格納されているかどうかを判定し、その結果におうじて画面の表示切り替えなどのユースケースが実行される。
   * ここでストアへのユーザ情報のロードは行っていないので、暗黙のうちにこの箇所が実行される前のどこかでユーザ情報が取得されていることが期待されている。
   * （もしユーザ情報が未取得・未完了のままここで判定されると、意図せぬユースケース実行が発生する可能性がある）
   * より明示的にするには、この箇所でもストアのロードを行うか（すでにロード済みであればAPI呼び出しはされない）、ユーザ情報を外から明示的なコンテキスト情報として注入するか、などの方法が考えられる。
   * @see https://github.com/my-color/growthpalette-front/pull/7682#discussion_r1381816619
   */
  public initialize(store: LoginStateStore): Either<Interaction<InitializeFailure>, Interaction<'initialized'>> {
    if (store.isRegistered()) {
      return left(this.transitTo('already-logged-in'))
    }

    return right(this.transitTo('initialized'))
  }

  public async login({ authentication, gateway }: {
    authentication: AuthenticationData,
    gateway: LoginGateway
  }): Promise<LoginResult> {
    if (this.state === 'not-initialized') {
      return left(
        this.transitTo('failed-not-initialized-yet')
      )
    }
    if (this.state === 'already-logged-in' || this.state === 'login-succeed') {
      return right(
        this.transitTo('login-succeed')
      )
    }

    return gateway.login(authentication).then((): LoginResult =>
      right(
        this.transitTo('login-succeed')
      )
    ).catch((error): LoginResult => {
      log.error(error)

      if (error instanceof TooManyRequestedError) {
        return left(this.transitTo('failed-too-many-requested'))
      }
      if (error instanceof IncorrectAuthenticationDataError) {
        return /[A-Z]/.test(authentication.email)
          ? left(this.transitTo('failed-incorrect-authentication-with-email-contains-uppercase'))
          : left(this.transitTo('failed-incorrect-authentication'))
      }

      return left(this.transitTo('failed-unknown'))
    })
  }

  private transitTo<S extends State>(s: S): Interaction<S> {
    return new Interaction<S>(s)
  }
}
